import { Theme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React, { FC } from 'react';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { messagingActions } from '../../reducers/messagingSlice';
import { AgentMode } from '../../redux/features/agentMode/agentMode.constants';
import { selectAgentMode } from '../../redux/features/agentMode/agentMode.selector';
import { setAgentMode } from '../../redux/features/agentMode/agentMode.slice';
import { selectIsShadowModeEnabled } from '../../redux/features/config/config.selector';
import Colors from '../../utils/color';
import { HITL3_FONT_SIZE } from '../../utils/hitl3Typography';

const MODES: AgentMode[] = [AgentMode.shadow, AgentMode.intercept];

/**
 * Built to the compact LanguageSwitch's metrics, not to its own.
 *
 * That switch is the console's existing answer to "two mutually exclusive
 * choices, side by side, on a light ground", and it was tuned once in HITL-1665
 * to sit in a row with the intent pills. Copying it means this control reads as
 * part of the same console rather than as a second opinion about what a
 * segmented control looks like: white track, one hairline, squared corners, and
 * the 34px height that the intent pills, Staff and Issue all share.
 *
 * Square throughout — no radius on the track and none on the segments. This is
 * not a chip sitting next to the status strip, it is the right-hand end OF the
 * strip: flush to the band on one side and to the console edge on the other,
 * full row height, with a single hairline where it meets the band. Any radius
 * on the track put porcelain notches into an otherwise continuous bar, and a
 * radius on the segments re-introduced the same softness one level in, which is
 * exactly the seam this arrangement exists to remove.
 *
 * The selected fill is `daintree` — `palette.primary.main`, which is the app
 * bar's own background, and the same colour the compact language switch picked
 * in HITL-1665 for the same reason: a selected segment that borrows the
 * header's colour belongs to the console instead of introducing another one.
 *
 * It replaced `amber`, and the deciding argument was the neighbour. This switch
 * sits against a band whose background is the AI's state — green, yellow, one
 * of three reds, and a two-red flash on a take-over. Amber has to hold its
 * meaning against all of those, and it cannot: beside the band's `acknowledge`
 * yellow the selected segment stops reading as a selection and starts reading
 * as part of the status. A fixed dark navy is the one fill that means the same
 * thing whatever the band is doing, which is the whole job of a mode indicator.
 */
const useStyles = makeStyles((theme: Theme) => ({
  track: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '2px',
    // Full row height rather than the 34px bar-control token: welded into the
    // strip, its height is the strip's, and a fixed 34 against the row's
    // ~34.2px minimum would leave a hairline of porcelain along one edge.
    height: '100%',
    // The 3px the language switch insets its segments by, kept — but paired
    // with real horizontal room so the block reads as a section of the bar and
    // not as a control crammed against the screen edge.
    padding: '3px 8px',
    backgroundColor: Colors.white,
    // ONE edge. `alto`, the hairline the language switch uses, on the side that
    // meets the band — that is the only boundary that needs stating. A full
    // border would draw a line down the console's right edge and along the top
    // and bottom of the strip, which is the seam this arrangement exists to
    // remove.
    borderLeft: `1px solid ${Colors.alto}`,
    boxSizing: 'border-box',
    // Never shrinks. The band's slot beside it takes minWidth:0 and the band's
    // message ellipses, so that is the element that yields — which is the whole
    // reason the switch lives here and not in the header, where nothing yielded
    // and the row overflowed into a scrollbar on car enter.
    flexShrink: 0,
  },
  // `AgentMode`'s values are already title case, so there is no textTransform
  // here — the labels render as Shadow / Intercept, matching the band's
  // sentence-case message rather than shouting beside it.
  segment: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    padding: '0 10px',
    border: 0,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    fontFamily: theme.typography.fontFamily,
    fontSize: HITL3_FONT_SIZE,
    // 500, not the language switch's bold. Bold pulls the eye to a control that
    // is only reporting which mode you are in, and it sits directly beside the
    // band's own 500 — two weights across a seam that is meant to read as one
    // continuous bar.
    fontWeight: 500,
    whiteSpace: 'nowrap',
    backgroundColor: 'transparent',
    // The language switch's own inactive grey.
    color: '#4f5153',
    '&:hover:not(:disabled)': {
      backgroundColor: 'rgba(0,0,0,0.06)',
    },
    // Matches the language switch's `.disabled`, which is the state Shadow
    // takes for the rest of the session once Intercept is chosen.
    '&:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
  },
  // The header's own background with white on it — see the note above. White on
  // daintree is ~15:1, so the selected label stays the most legible text in the
  // row no matter what colour the band beside it has turned.
  segmentOn: {
    backgroundColor: Colors.daintree,
    color: Colors.white,
    '&:hover:not(:disabled)': {
      backgroundColor: Colors.daintree,
      color: Colors.white,
    },
  },
}));

/**
 * HITL-1604 — the supervising agent's mode, in the console status row beside
 * the AI status band.
 *
 * Pressing a segment **acts on nobody's behalf**. The mode is permission: it
 * unlocks the agent's surfaces, while the wire events that speak or order stay
 * where they already are, on the agent's real actions. Acting here would fail
 * dangerous — an accidental press would silence the AI and leave nobody serving
 * the customer.
 *
 * The one thing a press does put on the wire is HITL-1605's `agent_mode_change`
 * metric, which controls nothing at either end; see `select()` below.
 *
 * Choosing Intercept is one-way for the session. Shadow is then shown disabled
 * rather than hidden, so the agent can see the state they are no longer in;
 * the reducer refuses the transition too, so the rule does not depend on the
 * button being unclickable.
 *
 * Both segments are always shown at full width. There is no collapsed form: the
 * row spans the console and the band beside it ellipses, so the switch cannot be
 * squeezed no matter how narrow the viewport or how long the status text. The
 * header placement needed a pill-and-menu fallback because nothing in that row
 * would yield; moving here removed the problem rather than managing it, and a
 * one-press Intercept matters most in exactly the cramped moments a menu would
 * have cost two interactions.
 */
const AgentModeSwitch: FC = () => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const isEnabled = useAppSelector(selectIsShadowModeEnabled);
  const mode = useAppSelector(selectAgentMode);

  if (!isEnabled) return null;

  const isLocked = mode === AgentMode.intercept;

  /**
   * HITL-1605 — the transition goes on the wire from here, at the press.
   *
   * Deliberately not a `useEffect` watching `selectAgentMode`. An effect fires
   * on remount, fires on the three session-start resets in the slice — which
   * must emit nothing, since every session begins in Shadow by contract and
   * returning there is not a decision anyone made — and would report a
   * transition the reducer refused, because `setAgentMode` returns early once
   * the mode is Intercept. At the press, `mode` and `next` are both in hand and
   * only a real, accepted, agent-initiated change reaches the dispatch.
   *
   * Order matters: the metric goes after `setAgentMode`, so it can only ever
   * record a change that actually happened.
   *
   * It emits the metric and **nothing else** — no `agent_interception`, no
   * `agent_first_interception`, no order, no TTS. That asymmetry is HITL-1604's
   * decision and this story does not weaken it; the interception still follows
   * later, from the agent's first real action, at that action's own call site.
   *
   * Because Intercept is one-way, this fires at most ONCE per session — which
   * is what makes it worth having. Paired with the `agent_interception` that
   * follows on the first real action, the gap between the two is how long the
   * agent sat armed before doing anything.
   */
  const select = (next: AgentMode) => {
    // Defence in depth, and unreachable today: the only segment a locked switch
    // leaves enabled is the one already selected, which the next guard catches.
    // It stays for the metric rather than the mode — the reducer already refuses
    // a walk-back, so a press that got here past a lost `disabled` would report
    // an Intercept → Shadow change that never happened.
    if (isLocked) return;
    // Re-pressing the current mode is not a transition, so it is not an event.
    if (next === mode) return;
    dispatch(setAgentMode(next));
    dispatch(
      messagingActions.sendAgentMetric({
        data: {
          metric: 'agent_mode_change',
          from: mode,
          to: next,
          // A press on the switch. The console never moves the mode by itself
          // today; when HITL-1650 arms Intercept off the AI's take-over alert,
          // that path emits the same metric with `system` and this one is
          // already distinguishable from it.
          trigger: 'agent',
        },
      })
    );
  };

  return (
    <div
      className={classes.track}
      role="group"
      aria-label="Agent mode"
      data-testid="agent-mode-switch"
      data-mode={mode}
    >
      {MODES.map((value) => {
        const isOn = mode === value;
        return (
          <button
            key={value}
            type="button"
            className={`${classes.segment}${
              isOn ? ` ${classes.segmentOn}` : ''
            }`}
            aria-pressed={isOn}
            data-testid={`agent-mode-segment-${value}`}
            // Disabled, not hidden: the agent should still see the mode they can
            // no longer return to.
            disabled={isLocked && !isOn}
            onClick={() => select(value)}
          >
            {value}
          </button>
        );
      })}
    </div>
  );
};

export default AgentModeSwitch;
